import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook } from "lucide-react";
import { trackPhoneCall } from "@/components/layout/WhatsAppButton";

const LOGO_URL = "/logo.png";

const allProducts = [
  "Seguro Auto", "Seguro Auto Premium", "Seguro Vida", "Seguro Empresarial",
  "Seguro Vida Empresarial", "Seguro Auto Frota", "Seguro Transporte de Cargas",
  "Seguro Caminhão", "Seguro Empresa Essencial",
  "Responsabilidade Civil Profissional", "Responsabilidade Civil Geral",
  "Seguro RC Adm. e Diretores (D&O)", "Seguro Garantia",
  "Seguro Riscos Cibernéticos", "Seguro Máquinas e Equipamentos",
  "Seguro Condomínio", "Seguro Imobiliária", "Seguro Residencial",
  "Seguro Agronegócio", "Previdência", "Consórcios", "Capital de Giro",
  "Financiamentos", "Empréstimo", "Seguro Moto", "Seguro Táxi",
  "Seguro Fiança", "Proteção Combinada", "Seguro Eventos", "Seguro Mudança",
  "Seguro Carta Verde", "Seguro Carta Azul", "Seguro Viagem",
  "Seguro Equipamentos Portáteis", "Seguro Foto e Vídeo",
  "Seguro Smart e Games", "Seguro Notebook e Tablet", "Seguro Celular",
  "Seguro Bike", "Cartão de Crédito",
];

export default function Footer() {
  return (
    <footer className="bg-navy text-slate-300" data-testid="footer">
      {/* Products SEO Band */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-wider mb-5">Todos os Nossos Produtos</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-8 xl:grid-cols-10 gap-x-6 gap-y-3">
            {allProducts.map((p, i) => (
              <Link key={i} to="/cotacao" className="text-[11px] text-slate-400 hover:text-gold transition-colors leading-tight">{p}</Link>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="Núcleo" className="h-14 w-auto" />
              <div>
                <span className="text-lg font-heading font-bold text-white block leading-tight">Núcleo</span>
                <span className="text-xs text-slate-400 font-medium tracking-wide uppercase">Corretora de Seguros</span>
              </div>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">
              Proteção e confiança desde 1993. Há mais de 30 anos ajudando pessoas, famílias e empresas em todo o Brasil a escolher seguros sólidos, com segurança e orientação profissional.
            </p>
            <a href="https://www.google.com/maps/search/N%C3%BAcleo+Corretora+de+Seguros" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-gold mb-4 hover:text-white transition-colors" data-testid="footer-google-rating">
              <span>&#9733;&#9733;&#9733;&#9733;&#9733;</span>
              <span className="text-slate-400 hover:text-white transition-colors">5.0 no Google</span>
            </a>
            <div className="flex items-center gap-4 mt-4">
              <a href="https://www.facebook.com/profile.php?id=100064945274971" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-800 hover:bg-brand text-slate-400 hover:text-white transition-all" data-testid="footer-facebook" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://www.instagram.com/nucleoseguros" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-800 hover:bg-brand text-slate-400 hover:text-white transition-all" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-wider mb-5">Seguros em Destaque</h4>
            <ul className="space-y-2.5">
              {[
                { label: "Seguro Auto", href: "/seguro-auto" },
                { label: "Seguro Empresarial", href: "/seguro-empresarial" },
                { label: "Seguro de Vida", href: "/seguro-de-vida" },
                { label: "Cotação Online", href: "/cotacao" },
              ].map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="text-sm text-slate-400 hover:text-white transition-colors">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Institutional */}
          <div>
            <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-wider mb-5">Institucional</h4>
            <ul className="space-y-2.5">
              {[
                { label: "Sobre a Núcleo", href: "/sobre" },
                { label: "Seguradoras Parceiras", href: "/seguradoras" },
                { label: "Assistência 24h", href: "/assistencia-24h" },
                { label: "Blog", href: "/blog" },
                { label: "Contato", href: "/contato" },
              ].map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="text-sm text-slate-400 hover:text-white transition-colors">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-wider mb-5">Contato</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                <div className="text-sm text-slate-400">
                  <p className="text-white font-medium text-xs mb-1">Belo Horizonte - MG</p>
                  <span>Rua Cura D'Ars, 1318<br />Barroca - CEP 30431-083</span>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                <div className="text-sm text-slate-400">
                  <p className="text-white font-medium text-xs mb-1">Goiânia - GO</p>
                  <span>Rua 34, 90, Sl. 1001-V<br />Setor Marista - CEP 74150-220</span>
                </div>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 text-gold shrink-0" />
                <div className="text-sm text-slate-400">
                  <a href="tel:+553125550207" onClick={() => trackPhoneCall('footer')} className="hover:text-white transition-colors block">(31) 2555-0207</a>
                  <a href="tel:+5562999642435" onClick={() => trackPhoneCall('footer')} className="hover:text-white transition-colors block">(62) 99964-2435</a>
                </div>
              </li>
              <li className="flex items-center gap-2.5">
                <Clock className="h-4 w-4 text-gold shrink-0" />
                <span className="text-sm text-slate-400">Seg - Sex: 7h às 19h</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-gold shrink-0" />
                <a href="mailto:falecom@nucleoseguros.com.br" className="text-sm text-slate-400 hover:text-white transition-colors">falecom@nucleoseguros.com.br</a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} Núcleo Corretora de Seguros. Todos os direitos reservados.</p>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-xs text-slate-500">
            <span>CNPJ 37.889.862/0001-27</span>
            <span className="hidden sm:inline text-slate-700">|</span>
            <span>SUSEP nº 10.0156582</span>
            <span className="hidden sm:inline text-slate-700">|</span>
            <span>Núcleo Corretora de Seguros Ltda.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
